import React, { FunctionComponent, ReactNode, Suspense } from "react";

type ImportFn = Parameters<typeof React.lazy>[0];
type Importer = {
  lazy: (importFn: ImportFn) => FunctionComponent;
};

/**
 * Create a loader with a defined fallback that can be reused with various imports.
 * You can use lazy and Suspense directly if you don't need the reuse.
 * e.g.
 *   const loader = makeLazyLoader(<Spinner />);
 *   const MyComponent = loader.lazy(() => import("./MyComponent");
 * The import must be provided as a function instead of a string so that it's
 * detected as a split bundle and not just a random string.
 */
export function makeLazyLoader(fallback: ReactNode = ""): Importer {
  return {
    lazy: (importFn: ImportFn): FunctionComponent => {
      const Component = React.lazy(importFn);
      return function (props) {
        return (
          <Suspense fallback={fallback}>
            <Component {...props} />
          </Suspense>
        );
      };
    },
  };
}
